/**
 * AHKAH Signature Site
 * 最小限のJS（CSSで実現できない部分のみ）
 */

// ============================================
// 設定定数
// ============================================
const CONFIG = {
  // ブレークポイント
  BREAKPOINT: {
    SP: 767,
    TABLET: 1024,
  },
  // スクロールアニメーション設定
  SCROLL: {
    SCRUB_SP: 0.5,
    SCRUB_PC: 1.5,
    DEBOUNCE_DELAY: 250,
    RAF_THROTTLE: true,
  },
  // ビデオ設定
  VIDEO: {
    CHECK_DELAY: 2000,
    FALLBACK_DELAY: 1000,
  },
  // マスクアニメーション設定
  MASK: {
    INITIAL: '0% -30%',
    FINAL: '25% 100%',
  },
  // コレクションナビゲーション設定
  COLLECTION_NAV: {
    ROOT_MARGIN: '-30% 0px -70% 0px',
    THRESHOLD: 0,
  },
  // プロダクト固定スクロール設定
  PRODUCT_FIXED: {
    GAP_MARGIN_RATIO: 0.3,
    MIN_GAP_RATIO: 0.3,
    UPDATE_DELAY: 100,
  },
  // コンセプトタイトルアニメーション設定
  CONCEPT_TITLE: {
    STAGGER: 0.1,
    DURATION: 0.8,
    EASE: 'power2.out',
    START: 'top 80%',
    END: 'top 50%',
    INITIAL_Y: 20,
  },
};

// ============================================
// ユーティリティ関数
// ============================================

/**
 * GSAPとScrollTriggerが利用可能かチェック
 */
let scrollTriggerConfigured = false;
function checkGSAPAvailable() {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn('GSAP or ScrollTrigger is not loaded');
    return false;
  }
  gsap.registerPlugin(ScrollTrigger);

  // invalidateOnRefreshをグローバル設定（一度だけ実行）
  if (!scrollTriggerConfigured) {
    ScrollTrigger.config({
      invalidateOnRefresh: true,
    });
    scrollTriggerConfigured = true;
  }

  return true;
}

/**
 * デバイスがSPかどうかを判定
 */
function isSp() {
  return window.innerWidth <= CONFIG.BREAKPOINT.SP;
}

/**
 * デバウンス関数
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * requestAnimationFrameを使ったスロットル
 */
function throttleRAF(func) {
  let rafId = null;
  return function (...args) {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        func(...args);
        rafId = null;
      });
    }
  };
}

// ============================================
// 初期化
// ============================================

/**
 * リロード時に常にページトップから表示されるようにする
 */
function initScrollToTop() {
  // ブラウザの自動スクロール復元を無効化
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // ページロード時に強制的にトップへスクロール
  window.scrollTo(0, 0);

  // ページロード完了後にも念のためトップへスクロール
  window.addEventListener('load', () => {
    window.scrollTo(0, 0);
  });
}

// リロード時のスクロール位置制御を初期化（最優先で実行）
initScrollToTop();

/**
 * ページフェードイン処理
 * DOM構築とJS初期化完了後に実行
 */
function initPageFadeIn() {
  const body = document.body;
  if (!body) return;

  // DOMContentLoadedとwindow.onloadの両方が完了したことを確認
  let domReady = false;
  let windowLoaded = false;

  function checkAndFadeIn() {
    if (domReady && windowLoaded) {
      // 少し遅延を入れてスムーズにフェードイン
      requestAnimationFrame(() => {
        body.classList.add('is-loaded');
      });
    }
  }

  // DOMContentLoadedの処理
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      domReady = true;
      checkAndFadeIn();
    });
  } else {
    domReady = true;
  }

  // window.onloadの処理
  if (document.readyState === 'complete') {
    windowLoaded = true;
    checkAndFadeIn();
  } else {
    window.addEventListener('load', () => {
      windowLoaded = true;
      checkAndFadeIn();
    });
  }

  // フォールバック: 一定時間経過後もフェードイン（リソース読み込みが遅い場合）
  setTimeout(() => {
    if (!body.classList.contains('is-loaded')) {
      body.classList.add('is-loaded');
    }
  }, 3000);
}

// ページフェードインを初期化
initPageFadeIn();

document.addEventListener('DOMContentLoaded', () => {
  initHeroVideoWrapperHeight();
  initHeroScroll()
  initHeroVideo();
  initCollectionNav();
  initModal();
  initProductSwiper();
  initProductFixedScroll();
  initHeroWhite1Animation();
  initHeroMaskAnimation();
  initHeroBackgroundAnimation();
  initHeroDarkBackgroundAnimation();
  initEventFadeIn();
  initHeroDarkBackgroundImage();
  initConceptTitleTextSplit();
  initLogoVisible();
});

/**
 * SP時にp-hero__video-wrapperにスクリーン高さを設定
 * デバイスのスクリーン高さを固定で割り当てる
 * PC幅になったらheightを解除
 */
function initHeroVideoWrapperHeight() {
  const videoWrappers = document.querySelectorAll('.p-hero__video-wrapper');
  if (videoWrappers.length === 0) return;

  // 高さを設定する関数
  const setHeight = () => {
    if (isSp()) {
      // SP時: スクリーンの高さを取得して設定
      const screenHeight = screen.height;
      videoWrappers.forEach((wrapper) => {
        wrapper.style.height = `${screenHeight}px`;
      });
    } else {
      // PC時: heightを解除
      videoWrappers.forEach((wrapper) => {
        wrapper.style.height = '';
      });
    }
  };

  // 初期設定
  setHeight();

  // リサイズ時の処理（デバウンス）
  const handleResize = debounce(() => {
    setHeight();
  }, CONFIG.SCROLL.DEBOUNCE_DELAY);

  window.addEventListener('resize', handleResize);
}

/**
 * スクロール時に.p-heroへ.is-scrollクラスを付与
 */
function initHeroScroll() {
  const hero = document.querySelector('.p-hero');
  if (!hero) return;

  const updateHeroScroll = throttleRAF(() => {
    hero.classList.toggle('is-scroll', window.scrollY > 0);
  });

  window.addEventListener('scroll', updateHeroScroll, { passive: true });
  updateHeroScroll(); // 初期状態をチェック
}

/**
 * ヒーローセクションのvideo要素の自動再生制御
 * 省電力モードでも確実に再生されるように複数の方法で試行
 * 再生できない場合は代替画像を表示
 */
function initHeroVideo() {
  const videos = document.querySelectorAll('.p-hero__video');
  if (videos.length === 0) return;

  /**
   * 動画ファイル名から代替画像のパスを生成
   */
  function getFallbackImagePath(videoSrc) {
    const fileName = videoSrc.split('/').pop().replace(/\.(mp4|webm|mov)$/i, '');
    const directory = videoSrc.substring(0, videoSrc.lastIndexOf('/'));
    return `${directory}/${fileName}.webp`;
  }

  /**
   * 代替画像を表示
   */
  function showFallbackImage(video) {
    if (video.dataset.fallbackShown === 'true') return;

    const videoWrapper = video.closest('.p-hero__video-wrapper');
    if (!videoWrapper) return;

    const videoSrc = video.querySelector('source')?.getAttribute('src') || video.src;
    if (!videoSrc) return;

    const img = document.createElement('img');
    img.src = getFallbackImagePath(videoSrc);
    img.alt = '';
    img.className = 'p-hero__video-fallback';
    img.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;z-index:1;';

    video.style.display = 'none';
    videoWrapper.appendChild(img);
    video.dataset.fallbackShown = 'true';
  }

  /**
   * 再生を試行
   */
  function attemptPlay(video) {
    if (!video || video.dataset.fallbackShown === 'true' || !video.paused) return;

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => showFallbackImage(video));
    } else {
      showFallbackImage(video);
    }
  }

  /**
   * すべてのvideo要素に対して再生を試みる
   */
  function playAllVideos() {
    videos.forEach(attemptPlay);
  }

  /**
   * 再生状態を確認（一定時間後に再生されていない場合は代替画像を表示）
   */
  function checkPlaybackStatus() {
    videos.forEach((video) => {
      if (video.dataset.fallbackShown === 'true') return;
      if (video.paused && video.readyState >= 2) {
        setTimeout(() => {
          if (video.paused && video.dataset.fallbackShown !== 'true') {
            showFallbackImage(video);
          }
        }, CONFIG.VIDEO.FALLBACK_DELAY);
      }
    });
  }

  // 初期再生試行
  playAllVideos();
  setTimeout(checkPlaybackStatus, CONFIG.VIDEO.CHECK_DELAY);

  // Intersection Observerでビューポートに入った時に再生を試みる
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) attemptPlay(entry.target);
    });
  }, { root: null, rootMargin: '0px', threshold: 0.1 });

  videos.forEach((video) => observer.observe(video));

  // 可視性変更時とフォーカス復帰時に再生を試みる
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) playAllVideos();
  });
  window.addEventListener('focus', playAllVideos);
}

/**
 * コレクションナビゲーションのアクティブ状態を
 * スクロール位置に応じて切り替える
 */
function initCollectionNav() {
  const navItems = document.querySelectorAll('.p-collection-nav__item');
  const sections = document.querySelectorAll('.p-collection__anchor-target[id]');

  if (navItems.length === 0 || sections.length === 0) return;

  /**
   * ナビゲーションのアクティブ状態を更新
   */
  function updateActiveNav(activeId) {
    navItems.forEach((item) => {
      const link = item.querySelector('a');
      item.classList.toggle('is-active', link?.getAttribute('href') === `#${activeId}`);
    });
  }

  /**
   * スクロール位置に基づいてアクティブなセクションを判定
   */
  function updateActiveFromScroll() {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    const triggerOffset = viewportHeight * 0.3; // ビューポート上部30%の位置をトリガー
    const triggerPoint = scrollY + triggerOffset;

    let activeSection = null;
    let maxScrollTop = -Infinity;

    // 各セクションの位置を取得
    const sectionData = Array.from(sections).map((section) => {
      const rect = section.getBoundingClientRect();
      const sectionTop = scrollY + rect.top;
      return {
        id: section.getAttribute('id'),
        top: sectionTop,
        element: section,
      };
    }).sort((a, b) => a.top - b.top); // 上から順にソート

    // トリガー位置を超えたセクションのうち、最も下にあるものを選択
    sectionData.forEach((data) => {
      if (data.top <= triggerPoint) {
        if (data.top > maxScrollTop) {
          maxScrollTop = data.top;
          activeSection = data.id;
        }
      }
    });

    // すべてのセクションがトリガー位置より下にある場合、最初のセクションを選択
    if (activeSection === null && sectionData.length > 0) {
      activeSection = sectionData[0].id;
    }

    // ページ最上部の場合、最初のセクションを選択
    if (scrollY < 100 && sectionData.length > 0) {
      activeSection = sectionData[0].id;
    }

    if (activeSection) {
      updateActiveNav(activeSection);
    }
  }

  // スクロールイベントで更新（throttleRAFを使用）
  const handleScroll = throttleRAF(updateActiveFromScroll);
  window.addEventListener('scroll', handleScroll, { passive: true });

  // 初期状態を設定
  updateActiveFromScroll();

  // リサイズ時も更新
  window.addEventListener('resize', debounce(() => {
    updateActiveFromScroll();
  }, CONFIG.SCROLL.DEBOUNCE_DELAY));
}

/**
 * プロダクトスライダーの初期化
 */
function initProductSwiper() {
  const imageSwipers = document.querySelectorAll('.product-swiper-image');
  if (imageSwipers.length === 0) return;

  imageSwipers.forEach((imageSwiperEl) => {
    const collection = imageSwiperEl.getAttribute('data-collection');
    const textSwiperEl = document.querySelector(`.product-swiper-text[data-collection="${collection}"]`);
    if (!textSwiperEl) return;

    // テキスト用swiper（フェードエフェクト）
    const textSwiper = new Swiper(textSwiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: true,
      loop: true,
      allowTouchMove: false,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 500,
      observer: true,
      observeParents: true,
    });

    // 画像用swiper
    const imageSwiper = new Swiper(imageSwiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: true,
      loop: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 40,
          centeredSlides: true,
          loop: true,
          watchSlidesProgress: true,
        },
      },
      navigation: {
        nextEl: imageSwiperEl.querySelector('.swiper-button-next'),
        prevEl: imageSwiperEl.querySelector('.swiper-button-prev'),
      },
      on: {
        slideChangeTransitionEnd() {
          // アニメーション完了後に同期（ループモードの場合はslideToLoopとrealIndexを使用）
          textSwiper.slideToLoop(this.realIndex);
        },
      },
    });

    // 初期化時に同期（ループモードの場合はslideToLoopとrealIndexを使用）
    textSwiper.slideToLoop(imageSwiper.realIndex);
  });
}

/**
 * マスクアニメーションの共通処理
 */
function createMaskAnimation(target, trigger, options = {}) {
  if (!checkGSAPAvailable() || !target || !trigger) return;

  const {
    start = 'top bottom',
    end = 'top top',
    endTrigger = null,
  } = options;

  gsap.set(target, { maskPosition: CONFIG.MASK.INITIAL });
  gsap.to(target, {
    maskPosition: CONFIG.MASK.FINAL,
    ease: 'none',
    scrollTrigger: {
      trigger,
      start,
      end,
      endTrigger,
      scrub: isSp() ? CONFIG.SCROLL.SCRUB_SP : CONFIG.SCROLL.SCRUB_PC,
    },
  });
}

/**
 * ヒーローセクションの「白1」背景色アニメーション
 * ウィンドウスクロール0の状態から
 * p-hero__subの底辺がウィンドウの上部に消えるまでのスクロール範囲で
 * mask-positionを-30%から100%に変化させる
 */
function initHeroWhite1Animation() {
  const hero = document.querySelector('.p-hero');
  const heroSub = document.querySelector('.p-hero__sub');
  const heroWhite1 = document.querySelector('.p-hero__video-wrapper--background.background-color-white-1');

  if (!hero || !heroSub || !heroWhite1) return;

  createMaskAnimation(heroWhite1, hero, {
    start: 'top top',
    endTrigger: heroSub,
    end: 'bottom top',
  });
}

/**
 * ヒーローセクションの背景動画レイヤー2のmask-positionアニメーション
 * .l-hero-spacer__innerの高さ中央がウィンドウの上端に到達した時から
 * .l-hero-spacer__innerの底辺がウィンドウの上端に到達するまで変化する
 */
function initHeroMaskAnimation() {
  const heroSpacer = document.querySelector('.l-hero-spacer__inner');
  const heroVideoWrapper = document.querySelector('.p-hero__video-wrapper--first');

  if (!heroSpacer || !heroVideoWrapper) return;

  createMaskAnimation(heroVideoWrapper, heroSpacer, {
    start: 'top top',
    end: 'bottom top',
  });
}

/**
 * ヒーローセクションの背景色アニメーション
 * l-event-wrapperが下から出現して画面の上へ消えるまでのスクロール範囲で
 * background-color-whiteのopacityとmask-positionを変化させる
 */
function initHeroBackgroundAnimation() {
  const eventWrapper = document.querySelector('.l-event-wrapper');
  const heroBackground = document.querySelector('.p-hero__video-wrapper--background.background-color-white');

  if (!eventWrapper || !heroBackground) return;

  const isMobile = window.matchMedia('(max-width: 767px)').matches;

  createMaskAnimation(heroBackground, eventWrapper, {
    start: isMobile ? 'top 260%' : 'top 80%',
    end: 'top top',
  });
}

/**
 * ヒーローセクションのダーク背景色アニメーション
 * p-collection.p-collection--darkが下から出現して画面の上へ消えるまでのスクロール範囲で
 * background-color-blackのmask-positionを変化させる
 */
function initHeroDarkBackgroundAnimation() {
  const darkCollection = document.querySelector('.p-collection.p-collection--dark');
  const heroDarkBackground = document.querySelector('.p-hero__video-wrapper--background.background-color-black');

  if (!darkCollection || !heroDarkBackground) return;

  createMaskAnimation(heroDarkBackground, darkCollection);
}

/**
 * p-gallery p-gallery--darkの上辺がウィンドウ上部に到達した時に
 * background-color-blackにhas-dark-gallery-bgクラスを付与して背景画像を変更
 */
function initHeroDarkBackgroundImage() {
  const darkGallery = document.querySelector('.p-gallery.p-gallery--dark');
  const heroDarkBackground = document.querySelector('.p-hero__video-wrapper--background.background-color-black');

  if (!darkGallery || !heroDarkBackground) return;

  // パディングの値を取得
  const computedStyle = window.getComputedStyle(darkGallery);
  const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

  // ScrollTriggerを使用してクラスを追加/削除
  if (checkGSAPAvailable()) {
    // 上辺（paddingの内側）がウィンドウ上部に到達した時にクラスを追加
    ScrollTrigger.create({
      trigger: darkGallery,
      start: `top+=${paddingTop} top`,
      onEnter: () => {
        heroDarkBackground.classList.add('has-dark-gallery-bg');
      },
      onLeaveBack: () => {
        heroDarkBackground.classList.remove('has-dark-gallery-bg');
      },
    });

    // 底辺（paddingの内側）がウィンドウ底辺に到達した時にクラスを追加
    ScrollTrigger.create({
      trigger: darkGallery,
      start: `bottom-=${paddingBottom} bottom`,
      onEnter: () => {
        heroDarkBackground.classList.add('has-dark-item-bg');
      },
      onLeaveBack: () => {
        heroDarkBackground.classList.remove('has-dark-item-bg');
      },
    });
  } else {
    // GSAPが利用できない場合のフォールバック（IntersectionObserver）
    // 上辺の検知（paddingの内側を考慮）
    const topObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rect = entry.boundingClientRect;
        // paddingの内側の上辺 = top + paddingTop
        const innerTop = rect.top + paddingTop;

        if (entry.isIntersecting && innerTop <= 0) {
          heroDarkBackground.classList.add('has-dark-gallery-bg');
        } else {
          heroDarkBackground.classList.remove('has-dark-gallery-bg');
        }
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    });

    // 底辺の検知（paddingの内側を考慮）
    const bottomObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rect = entry.boundingClientRect;
        const windowHeight = window.innerHeight;
        // paddingの内側の底辺 = bottom - paddingBottom
        const innerBottom = rect.bottom - paddingBottom;

        if (entry.isIntersecting && innerBottom >= windowHeight) {
          heroDarkBackground.classList.add('has-dark-item-bg');
        } else {
          heroDarkBackground.classList.remove('has-dark-item-bg');
        }
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: [0, 1],
    });

    topObserver.observe(darkGallery);
    bottomObserver.observe(darkGallery);
  }
}

/**
 * モーダルの開閉制御
 */
function initModal() {
  const modal = document.querySelector('.p-modal');
  const modalBlur = document.querySelector('.p-modal-blur');
  const galleryItems = document.querySelectorAll('.p-gallery__item');
  const imgUnits = document.querySelectorAll('.p-gallery__item .c-img-unit');
  const closeButton = document.querySelector('.p-modal__close');
  const overlay = document.querySelector('.p-modal__overlay');
  const modalContent = document.querySelector('.p-modal__content');
  const modalItems = document.querySelectorAll('.p-modal__items');
  const fixedHeader = document.querySelector('.p-fixed-header');

  if (!modal) return;

  let currentItemIndex = null;
  let scrollPosition = 0;

  /**
   * モーダルを開く（指定されたインデックスのアイテムを表示）
   */
  function openModal(itemIndex) {
    currentItemIndex = itemIndex;
    const displayValue = isSp() ? 'flex' : 'grid';
    modalItems.forEach((item, index) => {
      item.style.display = index === itemIndex ? displayValue : 'none';
    });
    modal.setAttribute('aria-hidden', 'false');
    if (modalBlur) {
      modalBlur.setAttribute('aria-hidden', 'false');
    }

    // 現在のスクロール位置を保存
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // 背景のスクロールを止める
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    // .p-fixed-headerを非表示にする
    if (fixedHeader) {
      fixedHeader.style.opacity = '0';
      fixedHeader.style.pointerEvents = 'none';
    }
  }

  /**
   * モーダルを閉じる
   */
  function closeModal() {
    currentItemIndex = null;
    modal.setAttribute('aria-hidden', 'true');
    if (modalBlur) {
      modalBlur.setAttribute('aria-hidden', 'true');
    }

    // 背景のスクロールを復元
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';

    // scroll-behavior: smooth の影響を回避するため、一時的に auto に変更
    const originalScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';

    // 保存したスクロール位置に戻す（即座にスクロール）
    window.scrollTo(0, scrollPosition);

    // 次のフレームで scroll-behavior を元に戻す
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = originalScrollBehavior || '';
      });
    });

    // .p-fixed-headerのスタイルを復元（CSSのルールに戻す）
    if (fixedHeader) {
      fixedHeader.style.opacity = '';
      fixedHeader.style.pointerEvents = '';
    }
  }

  /**
   * リサイズ時に表示中のモーダルアイテムのdisplayプロパティを更新
   */
  function updateModalDisplay() {
    if (currentItemIndex !== null && modal.getAttribute('aria-hidden') === 'false') {
      const displayValue = isSp() ? 'flex' : 'grid';
      modalItems.forEach((item, index) => {
        if (index === currentItemIndex && item.style.display !== 'none') {
          item.style.display = displayValue;
        }
      });
    }
  }

  // c-img-unitにクリックイベントを追加
  imgUnits.forEach((imgUnit, index) => {
    imgUnit.addEventListener('click', (e) => {
      e.preventDefault();
      openModal(index);
    });
  });

  // 閉じるボタン
  closeButton?.addEventListener('click', (e) => {
    e.preventDefault();
    closeModal();
  });

  /**
   * クリックされた要素が画像やテキストなどの特定の要素かどうかを判定
   */
  function isClickableElement(target) {
    // 閉じるボタン
    if (target.closest('.p-modal__close')) return true;
    // 画像要素
    if (target.closest('.p-modal__item-img')) return true;
    // テキスト情報要素
    if (target.closest('.p-modal__item-info')) return true;
    // アイテム要素自体
    if (target.closest('.p-modal__item')) return true;
    return false;
  }

  // オーバーレイクリックで閉じる
  overlay?.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });

  // モーダルコンテンツ内の空白部分をクリックで閉じる
  modalContent?.addEventListener('click', (e) => {
    // クリックされた要素が画像やテキストなどの特定の要素でない場合のみ閉じる
    if (!isClickableElement(e.target)) {
      closeModal();
    }
  });

  // ESCキーで閉じる
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  });

  // リサイズ時にdisplayプロパティを更新（幅のみ監視）
  let lastModalWidth = window.innerWidth;
  const handleModalResize = debounce(() => {
    const currentWidth = window.innerWidth;
    if (currentWidth !== lastModalWidth) {
      lastModalWidth = currentWidth;
      updateModalDisplay();
    }
  }, CONFIG.SCROLL.DEBOUNCE_DELAY);
  window.addEventListener('resize', handleModalResize);
}

/**
 * コンセプトタイトルのテキストを行ごとに分割
 * SplitTextを使用
 */
function initConceptTitleTextSplit() {
  const targets = document.querySelectorAll('.p-concept-title__text--large, .p-concept-title__text');
  if (targets.length === 0) return;

  const splitInstances = new Map();
  const wrapperElements = new Map();

  /**
   * テキストを分割
   */
  function splitText() {
    targets.forEach((target) => {
      if (splitInstances.has(target)) {
        // 既存のラッパー要素を削除
        const existingWrappers = wrapperElements.get(target);
        if (existingWrappers) {
          existingWrappers.forEach((wrapper) => {
            if (wrapper.parentNode) {
              wrapper.parentNode.replaceChild(wrapper.firstChild, wrapper);
            }
          });
        }
        splitInstances.get(target).revert();
        wrapperElements.delete(target);
      }

      const splitInstance = new SplitText(target, {
        type: 'lines',
        linesClass: 'split-line',
        tag: 'div',
      });

      // 各split-lineをラップする親要素を追加
      const wrappers = [];
      if (splitInstance.lines && splitInstance.lines.length > 0) {
        splitInstance.lines.forEach((line) => {
          const wrapper = document.createElement('div');
          wrapper.className = 'split-line-wrapper';
          line.parentNode.insertBefore(wrapper, line);
          wrapper.appendChild(line);
          wrappers.push(wrapper);
        });
      }

      splitInstances.set(target, splitInstance);
      wrapperElements.set(target, wrappers);
    });

    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  }

  /**
   * 分割とアニメーションを初期化
   */
  function initSplitAndAnimation() {
    splitText();
    requestAnimationFrame(initConceptTitleTextAnimation);
  }

  // 初期化
  initSplitAndAnimation();

  // リサイズ時の処理（デバウンス、幅のみ監視）
  let lastWidth = window.innerWidth;
  const handleResize = debounce(() => {
    const currentWidth = window.innerWidth;
    if (currentWidth !== lastWidth) {
      lastWidth = currentWidth;
      initSplitAndAnimation();
    }
  }, CONFIG.SCROLL.DEBOUNCE_DELAY);

  window.addEventListener('resize', handleResize);
}

/**
 * .p-collection__productの固定スクロール制御（常にfixed）
 * - 常にfixedで画面中央に固定
 * - ビューポート中央に最も近いコレクションセクションのプロダクトのみ表示
 * - 前後のセクションがレイヤー的に出入りする
 */
function initProductFixedScroll() {
  const productElements = document.querySelectorAll('.p-collection__product');
  if (productElements.length === 0) return;

  // プロダクトデータの初期化
  const productData = Array.from(productElements).map((product, index) => {
    const collection = product.closest('.p-collection');
    if (!collection) return null;

    const isFirst = index === 0;
    const isSecond = index === 1;

    if (isFirst) product.classList.add('is-first');
    if (isSecond) product.classList.add('is-sticky');

    return {
      product,
      collection,
      gallery: collection.querySelector('.p-gallery'),
      header: collection.querySelector('.p-collection__header'),
      nextCollection: collection.nextElementSibling?.closest('.p-collection'),
      prevCollection: collection.previousElementSibling?.closest('.p-collection'),
      productHeight: 400,
      isFirst,
      isSticky: isSecond,
      initialTop: null,
      initialProductAreaTop: null,
      fadeTween: null, // フェードアニメーション用
    };
  }).filter(Boolean);

  if (productData.length === 0) return;

  // キャッシュ用の変数
  let cachedRects = new Map();

  /**
   * getBoundingClientRectの結果をキャッシュして取得
   */
  function getCachedRect(element) {
    if (!element) return null;
    const key = element;
    if (!cachedRects.has(key)) {
      cachedRects.set(key, element.getBoundingClientRect());
    }
    return cachedRects.get(key);
  }

  /**
   * キャッシュをクリア
   */
  function clearRectCache() {
    cachedRects.clear();
  }

  /**
   * 各プロダクトの高さを取得
   */
  function updateProductHeights() {
    productData.forEach((data) => {
      const { product, isSticky } = data;
      const originalClasses = product.className;
      product.classList.remove('is-visible');

      if (!isSticky) product.style.position = 'static';
      product.style.visibility = 'visible';
      product.style.opacity = '1';

      void product.offsetHeight; // 強制再計算

      const height = product.offsetHeight || product.scrollHeight;
      if (height > 0) data.productHeight = height;

      product.className = originalClasses;
      if (!isSticky) product.style.position = '';
      product.style.visibility = '';
      product.style.opacity = '';
    });
  }

  // 初期高さを取得
  const initHeights = () => {
    setTimeout(updateProductHeights, CONFIG.PRODUCT_FIXED.UPDATE_DELAY);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeights);
  } else {
    initHeights();
  }

  // リサイズ時の処理（デバウンス、幅のみ監視）
  let lastProductWidth = window.innerWidth;
  const handleResize = debounce(() => {
    const currentWidth = window.innerWidth;
    if (currentWidth !== lastProductWidth) {
      lastProductWidth = currentWidth;
      clearRectCache();
      updateProductHeights();
      updateProductPositions();
    }
  }, CONFIG.SCROLL.DEBOUNCE_DELAY);
  window.addEventListener('resize', handleResize);

  /**
   * 前後の要素の位置を取得
   */
  function getElementPositions(data) {
    const { gallery, header, nextCollection, product, collection } = data;
    const productArea = product.closest('.p-collection__product-area');

    // 前の要素の下端位置
    let prevElementBottom = -Infinity;
    if (gallery) {
      prevElementBottom = getCachedRect(gallery)?.bottom ?? -Infinity;
    } else if (header) {
      prevElementBottom = getCachedRect(header)?.bottom ?? -Infinity;
    }

    // 次の要素の上端位置
    let nextElementTop = Infinity;
    if (nextCollection) {
      nextElementTop = getCachedRect(nextCollection)?.top ?? Infinity;
    } else if (productArea) {
      nextElementTop = getCachedRect(productArea)?.bottom ?? Infinity;
    } else {
      nextElementTop = getCachedRect(collection)?.bottom ?? Infinity;
    }

    return { prevElementBottom, nextElementTop };
  }

  /**
   * プロダクトの表示条件を評価
   */
  function evaluateProductVisibility(data) {
    const { productHeight } = data;
    const viewportCenter = window.innerHeight / 2;
    const viewportHeight = window.innerHeight;
    const { prevElementBottom, nextElementTop } = getElementPositions(data);

    const margin = viewportHeight * CONFIG.PRODUCT_FIXED.GAP_MARGIN_RATIO;
    const hasGap = prevElementBottom < viewportCenter + margin && nextElementTop > viewportCenter - margin;

    const gapSize = nextElementTop - prevElementBottom;
    const hasEnoughGap = gapSize >= viewportHeight || gapSize > productHeight * CONFIG.PRODUCT_FIXED.MIN_GAP_RATIO;

    const gapCenter = (prevElementBottom + nextElementTop) / 2;
    const distanceFromCenter = Math.abs(gapCenter - viewportCenter);

    return {
      ...data,
      shouldShow: hasGap && hasEnoughGap,
      distanceFromCenter,
      prevElementBottom,
      nextElementTop,
    };
  }

  /**
   * プロダクト位置を更新
   */
  function updateProductPositions() {
    clearRectCache(); // フレームごとにキャッシュをクリア

    const productEvaluations = productData.map(evaluateProductVisibility);
    const visibleProducts = productEvaluations.filter(item => item.shouldShow);
    const activeProduct = visibleProducts.length > 0
      ? visibleProducts.reduce((closest, current) =>
        current.distanceFromCenter < closest.distanceFromCenter ? current : closest
      )
      : null;

    productData.forEach((data) => {
      const { product, isFirst, isSticky } = data;
      const isActive = activeProduct && activeProduct.product === product;

      if (isFirst) {
        updateFirstProduct(data, isActive);
      } else if (isSticky) {
        updateSecondProduct(data, isActive);
      }
    });
  }

  // スクロールイベント（throttleRAFを使用）
  const handleScroll = throttleRAF(updateProductPositions);
  window.addEventListener('scroll', handleScroll, { passive: true });

  // 初期状態をチェック
  updateProductPositions();
  setTimeout(() => {
    updateProductHeights();
    updateProductPositions();
  }, CONFIG.PRODUCT_FIXED.UPDATE_DELAY);

  /**
   * 前後のセクションが覆いかぶさる判定（共通処理）
   */
  function checkOverlayingSections(data, isActive, productHeight) {
    const { product, gallery, header, nextCollection, prevCollection } = data;
    const viewportCenter = window.innerHeight / 2;
    const halfHeight = productHeight / 2;

    if (!isActive) {
      if (gallery) gallery.classList.remove('is-overlaying');
      if (header) header.classList.remove('is-overlaying');
      return;
    }

    // ギャラリーの判定
    if (gallery) {
      const galleryRect = getCachedRect(gallery);
      if (galleryRect && galleryRect.bottom > viewportCenter - halfHeight) {
        gallery.classList.add('is-overlaying');
        product.classList.add('is-covered');
      } else {
        gallery.classList.remove('is-overlaying');
      }
    }

    // ヘッダーの判定
    if (header) {
      const headerRect = getCachedRect(header);
      if (headerRect && headerRect.top < viewportCenter + halfHeight) {
        header.classList.add('is-overlaying');
        product.classList.add('is-covered');
      } else {
        header.classList.remove('is-overlaying');
      }
    }

    // 次のコレクションの判定
    if (nextCollection) {
      const nextRect = getCachedRect(nextCollection);
      if (nextRect && nextRect.top < viewportCenter + halfHeight) {
        product.classList.add('is-covered');
      } else {
        product.classList.remove('is-covered');
      }
    }

    // 前のコレクションの判定
    if (prevCollection) {
      const prevRect = getCachedRect(prevCollection);
      if (prevRect && prevRect.bottom > viewportCenter - halfHeight) {
        product.classList.add('is-covered');
      } else {
        product.classList.remove('is-covered');
      }
    }
  }

  /**
   * 1個目のプロダクトの更新処理（通常のfixed動作のみ）
   * スペーサー要素の底辺がウィンドウの下部に到達したタイミングで非表示にする
   * フェードアウト/フェードインアニメーションを実装
   */
  function updateFirstProduct(data, isActive) {
    const { product, productHeight, fadeTween } = data;

    // スペーサー要素を取得
    const spacer = document.querySelector('.p-collection__spacer');

    // スペーサーの底辺がウィンドウの下部に到達したかチェック
    let shouldHideBySpacer = false;
    if (spacer) {
      const spacerRect = getCachedRect(spacer);
      if (spacerRect && spacerRect.bottom <= window.innerHeight) {
        shouldHideBySpacer = true;
      }
    }

    // スペーサーの条件を満たした場合は非表示、それ以外は通常のisActive判定
    const finalIsActive = shouldHideBySpacer ? false : isActive;

    // 既存のアニメーションを停止
    if (fadeTween) {
      fadeTween.kill();
      data.fadeTween = null;
    }

    // 画像用Swiperのアクティブスライドを取得
    const imageSwiperEl = product.querySelector('.product-swiper-image');
    const activeSlide = imageSwiperEl?.querySelector('.swiper-slide-active');

    if (!finalIsActive) {
      // フェードアウト
      if (checkGSAPAvailable()) {
        // 親要素とアクティブなスライドを同時にフェードアウト
        const targets = activeSlide ? [product, activeSlide] : [product];
        data.fadeTween = gsap.to(targets, {
          opacity: 0,
          duration: 0.5,
          ease: 'power2.out',
          onComplete: () => {
            product.classList.remove('is-visible', 'is-covered');
            data.fadeTween = null;
          },
        });
      } else {
        product.style.opacity = '0';
        product.classList.remove('is-visible', 'is-covered');
      }
      checkOverlayingSections(data, false, productHeight);
      return;
    }

    // フェードイン
    product.classList.add('is-visible');
    if (checkGSAPAvailable()) {
      // 親要素とアクティブなスライドを同時にフェードイン
      const targets = activeSlide ? [product, activeSlide] : [product];
      data.fadeTween = gsap.to(targets, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
          data.fadeTween = null;
        },
      });
    } else {
      product.style.opacity = '1';
    }

    product.classList.remove('is-covered');
    checkOverlayingSections(data, true, productHeight);
  }

  /**
   * 2個目のプロダクトの更新処理（is-scrolling関連の処理を含む）
   */
  function updateSecondProduct(data, isActive) {
    const { product, productHeight } = data;
    // l-info-spacerが追加されたため、判定基準をl-info-spacerに変更
    const infoSpacer = document.querySelector('.l-info-spacer');
    const infoSection = document.querySelector('.p-info');

    // l-info-spacerが存在する場合はそれを基準に、なければ従来通り.p-infoを基準にする
    const triggerElement = infoSpacer || infoSection;
    if (!triggerElement) return;

    const triggerRect = getCachedRect(triggerElement);
    const isInfoVisible = triggerRect && triggerRect.top <= window.innerHeight - 500;
    const productArea = product.closest('.p-collection__product-area');

    if (isInfoVisible) {
      // is-scrollingモード
      const wasScrolling = product.classList.contains('is-scrolling');

      if (!wasScrolling && productArea) {
        // 初期位置を設定（初回のみ）
        const productRect = getCachedRect(product);
        const productAreaRect = getCachedRect(productArea);

        if (productRect && productAreaRect) {
          const centerY = productRect.top + productRect.height / 2;
          data.initialTop = centerY;
          data.initialProductAreaTop = productAreaRect.top;
          product.style.top = `${centerY}px`;
        }
      }

      product.classList.add('is-scrolling', 'is-visible');
      checkOverlayingSections(data, true, productHeight);

      // スクロール中の位置更新
      if (productArea && data.initialTop !== null && data.initialProductAreaTop !== null) {
        const productAreaRect = getCachedRect(productArea);
        if (productAreaRect) {
          const productAreaTopDiff = productAreaRect.top - data.initialProductAreaTop;
          product.style.top = `${data.initialTop + productAreaTopDiff}px`;
        }
      }
    } else {
      // 通常モード
      product.classList.remove('is-scrolling');
      product.style.top = '';
      data.initialTop = null;
      data.initialProductAreaTop = null;

      product.classList.toggle('is-visible', isActive);
      if (!isActive) {
        product.classList.remove('is-covered');
      }
      checkOverlayingSections(data, isActive, productHeight);
    }
  }
}

/**
 * p-hero__sub要素の底辺がウィンドウの上部に消えた際に
 * bodyにlogo-visibleクラスを追加
 */
function initLogoVisible() {
  if (!checkGSAPAvailable()) return;

  const heroSub = document.querySelector('.p-hero__sub');
  const body = document.body;

  if (!heroSub || !body) return;

  ScrollTrigger.create({
    trigger: heroSub,
    start: 'bottom top',
    onEnter: () => {
      body.classList.add('logo-visible');
    },
    onLeaveBack: () => {
      body.classList.remove('logo-visible');
    },
  });
}

/**
 * コンセプトタイトルのテキストアニメーション
 * スクロール時に指定された順番で表示
 * 順番: kanji → pronunciation → text--large各行 → text各行
 */
function initConceptTitleTextAnimation() {
  if (!checkGSAPAvailable()) return;

  const content = document.querySelector('.p-concept-title__content');
  if (!content) return;

  // アニメーション対象を順番に取得
  const kanji = content.querySelector('.p-concept-title__kanji');
  const pronunciation = content.querySelector('.p-concept-title__pronunciation');
  const textLargeLines = content.querySelectorAll('.p-concept-title__text--large .split-line');
  const textLines = content.querySelectorAll('.p-concept-title__text:not(.p-concept-title__text--large) .split-line');

  const animationElements = [
    ...(kanji ? [kanji] : []),
    ...(pronunciation ? [pronunciation] : []),
    ...Array.from(textLargeLines),
    ...Array.from(textLines),
  ];

  if (animationElements.length === 0) return;

  // 既存のScrollTriggerとアニメーションをクリア
  ScrollTrigger.getAll().forEach(trigger => {
    if (trigger.vars?.trigger === content) trigger.kill();
  });
  animationElements.forEach(element => gsap.killTweensOf(element));

  // 初期状態を設定
  gsap.set(animationElements, {
    opacity: 0,
    y: CONFIG.CONCEPT_TITLE.INITIAL_Y,
  });

  // アニメーション実行
  gsap.to(animationElements, {
    opacity: 1,
    y: 0,
    duration: CONFIG.CONCEPT_TITLE.DURATION,
    ease: CONFIG.CONCEPT_TITLE.EASE,
    stagger: CONFIG.CONCEPT_TITLE.STAGGER,
    scrollTrigger: {
      trigger: content,
      start: CONFIG.CONCEPT_TITLE.START,
      end: CONFIG.CONCEPT_TITLE.END,
      toggleActions: 'play none none none',
    },
  });
}

/**
 * ページ全体のjs-fade-in要素をスクロールに合わせてフェードイン
 * 各要素がビューポートに入ったときにis-activeクラスを追加
 */
function initEventFadeIn() {
  if (!checkGSAPAvailable()) return;

  // ページ全体のjs-fade-in要素を取得
  const fadeElements = document.querySelectorAll('.js-fade-in');
  if (fadeElements.length === 0) return;

  // 各要素に対してScrollTriggerを作成
  fadeElements.forEach((element) => {
    ScrollTrigger.create({
      trigger: element,
      start: 'top 80%',
      onEnter: () => {
        element.classList.add('is-active');
      },
      once: true, // 一度だけ実行
    });
  });
}
